import { Target } from "lucide-mui";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import {
  api,
  type ProspectListContactEntry,
  type Signal,
  type SignalProspectList,
  type StepExecution,
} from "../../../api/client.ts";
import { FoldList, FoldRow, useOpenFolds } from "../../../components/FoldList.tsx";
import IdentityCell from "../../../components/IdentityCell.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import UserAvatar from "../../../components/UserAvatar.tsx";
import { keys } from "../../../api/queryKeys.ts";
import { collectionPath, useTenant } from "../../../contexts/TenantContext.tsx";
import DetailDrawer from "../../../components/DetailDrawer.tsx";
import ExecutionStatusChip, { executionGist } from "../../../components/workflows/ExecutionStatusChip.tsx";
import {
  ContinueStepButton,
  ContinueStepsSplitButton,
  useContinuableSteps,
} from "../../../components/workflows/StepApprovalBanner.tsx";
import ShortcutHints from "../../../components/review/ShortcutHints.tsx";
import SignalResearchPanel from "../../../components/signals/SignalResearchPanel.tsx";
import { useSignalResearch } from "../../../components/signals/useSignalResearch.ts";
import { REVIEW_LABELS, useContinueSignals } from "../../../components/signals/signalReview.ts";
import { MATCH_STRENGTH_SCALE } from "../../../components/signals/MatchStrengthSelect.tsx";
import { formatNumber } from "../../../utils/format.ts";
import { isJobActive } from "../../../utils/status.ts";

// Labels the contact research agent sets on each person, in display order.
const CONTACT_LABELS = ["Seniority", "Priority", "Source"];

function ContactsSection({
  contacts,
  lists,
}: {
  contacts: ProspectListContactEntry[];
  lists: { id: string; name: string }[];
}) {
  const { tenantPath } = useTenant();
  const listName = (id: string | null) => lists.find((l) => l.id === id)?.name;
  return (
    <Stack spacing={1}>
      {contacts.map((c) => (
        <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <IdentityCell
                avatar={<UserAvatar displayName={c.contact_name} size={28} tooltip={false} />}
                name={c.contact_name ?? "Unnamed contact"}
                caption={c.title ?? c.labels.Role}
                to={tenantPath(`/contacts/${c.master_contact_id}`)}
              />
            </Box>
            {CONTACT_LABELS.filter((k) => c.labels[k]).map((k) => (
              <Chip key={k} size="small" variant="outlined" label={`${k}: ${c.labels[k]}`} />
            ))}
          </Stack>
          {c.inclusion_reason && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {c.inclusion_reason}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {[c.reachability.email, c.reachability.phone].filter(Boolean).join(" · ") ||
              "No email or phone yet"}
            {c.collection_id && (
              <>
                {" · on "}
                <Link component={RouterLink} to={tenantPath(collectionPath(c.collection_id))}>
                  {listName(c.collection_id) ?? "a collection"}
                </Link>
              </>
            )}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
}

function ListsSection({ lists }: { lists: SignalProspectList[] }) {
  const { tenantPath } = useTenant();
  return (
    <Stack spacing={0.5}>
      {lists.map((l) => (
        <Stack key={l.id} direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Link component={RouterLink} to={tenantPath(collectionPath(l.id))} variant="body2">
            {l.name}
          </Link>
          {l.review_state && (
            <Chip
              size="small"
              variant="outlined"
              color={l.review_state === "accepted" ? "success" : "default"}
              label={REVIEW_LABELS[l.review_state]}
            />
          )}
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {l.holding
              ? "The workflow's collection: every buyer its research named, and the people found there"
              : "Added here by a workflow step"}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * What can be done with the open signal here: continue a parked step.
 * Deciding on the company happens in Prospects; the chip opens it there.
 */
function DrawerActions({
  signal,
  prospectId,
  executions,
  approve,
  onDecided,
}: {
  signal: Signal;
  prospectId: string | null | undefined;
  executions: StepExecution[];
  approve: (executionId: string) => Promise<unknown>;
  onDecided: () => void;
}) {
  const { tenantPath } = useTenant();
  const continueSteps = useContinueSignals();
  const { steps, sourceLabel } = useContinuableSteps(executions);
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {prospectId ? (
          <Chip
            component={RouterLink}
            to={tenantPath(`/prospects/inbox?show=all&prospect=${prospectId}`)}
            clickable
            size="small"
            color={signal.prospect_review_state === "accepted" ? "success" : "default"}
            variant="outlined"
            icon={<Target />}
            label={
              signal.prospect_review_state
                ? `${REVIEW_LABELS[signal.prospect_review_state]} · ${signal.prospect_hit_count ?? 1} ${
                    signal.prospect_hit_count === 1 ? "hit" : "hits"
                  }`
                : "Open prospect"
            }
          />
        ) : (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            No company to decide on yet
          </Typography>
        )}
      </Box>
      {steps.length === 1 && (
        <ContinueStepButton execution={steps[0]} approve={approve} onDecided={onDecided} sourceLabel={sourceLabel} />
      )}
      {steps.length > 1 && (
        <ContinueStepsSplitButton
          steps={steps}
          approve={approve}
          onDecided={onDecided}
          sourceLabel={sourceLabel}
          onContinueAll={() => continueSteps.mutate([signal.id])}
          continuingAll={continueSteps.isPending}
        />
      )}
    </Stack>
  );
}

interface Props {
  signal: Signal | null;
  canResearch: boolean;
  dataTypeLabel: (slug: string) => string;
  recordLink: string | null;
  onClose: () => void;
  // The signal after / before this one in the list (J / ↓, K / ↑).
  onStep?: (direction: 1 | -1) => void;
}

/**
 * A signal in the log: the matcher's take, its research (start or re-run it,
 * every brief and run), the people it reached and, folded at the bottom, the
 * collections it reached and its steps.
 */
export default function SignalResearchDrawer({
  signal,
  canResearch,
  dataTypeLabel,
  recordLink,
  onClose,
  onStep,
}: Props) {
  const queryClient = useQueryClient();
  const { tenantPath } = useTenant();
  const signalId = signal?.id ?? "";
  const { data } = useSignalResearch(signal?.id);
  // Collections and workflow steps, folded at the bottom; folded again on the next signal.
  const folds = useOpenFolds(signalId);

  const contacts = data?.contacts ?? [];
  const lists = data?.collections ?? [];
  const executions = data?.executions ?? [];
  const latestBrief = data?.briefs[0];
  const refreshAfterDecision = () => {
    queryClient.invalidateQueries({ queryKey: keys.signal.research(signalId) });
    queryClient.invalidateQueries({ queryKey: keys.signal.all });
  };
  const running = (data?.runs ?? []).some((r) => isJobActive(r.status));
  // The research agent's buyer is the signal's company; the brief has it before
  // the list is refetched, and also when the buyer is not in the database.
  const companyId = latestBrief?.buyer_master_company_id ?? signal?.master_company_id;
  const companyName =
    latestBrief?.buyer_name ?? signal?.master_company_name ?? signal?.research_buyer_name;

  if (!signal) {
    return <DetailDrawer open={false} onClose={onClose} title="">{null}</DetailDrawer>;
  }
  return (
    <DetailDrawer
      open
      onClose={onClose}
      onStep={onStep}
      overline={`${dataTypeLabel(signal.data_type)} · ${signal.workflow_name ?? "signal"}`}
      // A record without a buyer (a Notion page) is still worth naming.
      title={
        companyName ?? signal.record_title ?? (running ? "Researching…" : "No buyer identified")
      }
      actions={
        canResearch && (
          <DrawerActions
            signal={signal}
            prospectId={data?.prospect_id ?? signal.prospect_id}
            executions={executions}
            approve={(executionId) => api.approveSignalStep(signal.id, executionId)}
            onDecided={refreshAfterDecision}
          />
        )
      }
      hints={onStep && <ShortcutHints canDecide={false} />}
    >
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Tooltip title={MATCH_STRENGTH_SCALE}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Signal matcher · match strength {signal.rating ?? "—"}/5
            </Typography>
          </Tooltip>
          <Typography variant="body2">{signal.reasoning}</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            {recordLink ? (
              <Link component={RouterLink} to={recordLink} variant="body2">
                Open record
              </Link>
            ) : (
              // No page in the app: the record's own (a Notion page).
              signal.record_url && (
                <Link href={signal.record_url} target="_blank" rel="noopener noreferrer" variant="body2">
                  Open record
                </Link>
              )
            )}
            {companyId && (
              <Link component={RouterLink} to={tenantPath(`/companies/${companyId}`)} variant="body2">
                Open company
              </Link>
            )}
          </Stack>
        </Paper>

        <Box>
          <SectionHeading title="Research" />
          <SignalResearchPanel signalId={signal.id} canResearch={canResearch} />
        </Box>

        {contacts.length > 0 && (
          <Box sx={{ pt: 1 }}>
            <SectionHeading title="Contacts" count={contacts.length} />
            <ContactsSection contacts={contacts} lists={lists} />
          </Box>
        )}

        {(lists.length > 0 || executions.length > 0) && (
          <Box sx={{ pt: 1 }}>
            <FoldList>
              {lists.length > 0 && (
                <FoldRow
                  label="Collections"
                  summary={`${formatNumber(lists.length)} · ${lists.map((l) => l.name).join(", ")}`}
                  expanded={folds.isOpen("collections")}
                  onToggle={() => folds.toggle("collections")}
                >
                  <ListsSection lists={lists} />
                </FoldRow>
              )}
              {executions.length > 0 && (
                <FoldRow
                  label="Workflow steps"
                  summary={executionGist(executions)}
                  expanded={folds.isOpen("steps")}
                  onToggle={() => folds.toggle("steps")}
                >
                  <Stack direction="row" sx={{ flexWrap: "wrap", gap: 1 }}>
                    {executions.map((e) => (
                      <ExecutionStatusChip key={e.id} execution={e} />
                    ))}
                  </Stack>
                </FoldRow>
              )}
            </FoldList>
          </Box>
        )}
      </Stack>
    </DetailDrawer>
  );
}
